<template>
  <div class="subscription-page" v-loading="loading">
    <!-- A区：头部概览卡片 -->
    <el-card class="overview-card" shadow="hover">
      <div slot="header" class="overview-header">
        <span class="overview-title">当前套餐</span>
        <div class="overview-actions">
          <el-button type="text" size="small" icon="el-icon-document" @click="openOrderDrawer">查看历史订单</el-button>
        </div>
      </div>
      <el-row :gutter="20">
        <el-col :span="12">
          <div class="overview-left">
            <div class="plan-name">基础套餐</div>
            <div class="plan-status">
              <el-tag size="small" :type="subscriptionTagType">{{ subscriptionTagText }}</el-tag>
            </div>
            <div class="plan-expiry" :class="{ 'expiring-soon': isExpiringSoon }">
              有效期至：{{ endTime || '-' }}
            </div>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="overview-right">
            <div class="staff-info">
              <div class="staff-label">技师名额</div>
              <div class="staff-count">{{ usedStaff }} / {{ totalStaff }}</div>
            </div>
            <el-progress
              :text-inside="true"
              :stroke-width="20"
              :percentage="usagePercentage"
              :status="usageStatus">
            </el-progress>
            <div class="staff-detail">
              基础名额 {{ baseStaffCount }} 人 + 额外购买 {{ extraStaffCount }} 人
            </div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- B区：核心交易面板 -->
    <el-card class="transaction-card" shadow="hover">
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <!-- Tab 1：套餐续费 -->
        <el-tab-pane label="套餐续费" name="renew">
          <div class="transaction-content">
            <el-row :gutter="20">
              <el-col :span="14">
                <div class="form-section">
                  <div class="section-title">选择续费时长</div>
                  <el-radio-group v-model="renewDuration" size="medium">
                    <el-radio-button :label="1">1个月</el-radio-button>
                    <el-radio-button :label="3">3个月</el-radio-button>
                    <el-radio-button :label="12">1年</el-radio-button>
                  </el-radio-group>
                  <div class="current-config">
                    <p>当前总名额：{{ totalStaff }} 人</p>
                    <p>实际使用：{{ usedStaff }} 人</p>
                    <p class="renew-hint">续费后总名额：{{ renewAfterTotalStaff }} 人（按实际使用人数续费）</p>
                    <p class="renew-warning" v-if="usedStaff < totalStaff">
                      <i class="el-icon-warning"></i> 续费后总名额将调整为 {{ renewAfterTotalStaff }} 人，如需更多名额请额外购买
                    </p>
                  </div>
                </div>
              </el-col>
              <el-col :span="10">
                <div class="price-section" v-loading="renewCalculateLoading">
                  <div class="price-title">费用明细</div>
                  <div class="price-item" v-if="renewPriceData">
                    <span>基础费：</span>
                    <span>${{ basePriceDisplay }}</span>
                  </div>
                  <div class="price-item" v-if="renewPriceData">
                    <span>额外名额费：</span>
                    <span>${{ extraStaffPrice }}</span>
                    <div class="price-detail" v-if="renewPriceData.current_extra_staff !== undefined">
                      （当前额外名额：{{ renewPriceData.current_extra_staff }}人）
                    </div>
                  </div>
                  <div class="price-item" v-if="!renewPriceData">
                    <span>基础费：</span>
                    <span>${{ basePrice }} × {{ renewDuration }}月 = ${{ basePriceDisplay }}</span>
                  </div>
                  <div class="price-item" v-if="!renewPriceData">
                    <span>额外名额费：</span>
                    <span>{{ renewExtraStaffCount }}人 × ${{ extraUnitPrice }} × {{ renewDuration }}月 = ${{ extraStaffPrice }}</span>
                  </div>
                  <div class="price-total">
                    <span>合计应付：</span>
                    <span class="total-amount">${{ renewTotalPrice }}</span>
                  </div>
                  <div class="price-hint" v-if="renewPriceData && renewPriceData.end_time">
                    续费后有效期至：{{ renewPriceData.end_time }}
                  </div>
                  <!-- 已到期也允许续费：不再受 isExpired 限制，仅在加载中或未算价完成时禁用 -->
                  <el-button
                    type="primary"
                    size="medium"
                    @click="handleRenew"
                    :disabled="loading || renewCalculateLoading || !renewPriceData"
                  >
                    立即支付
                  </el-button>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-tab-pane>

        <!-- Tab 2：购买名额（已到期时禁用） -->
        <el-tab-pane label="购买名额" name="addStaff" :disabled="isExpired">
          <div class="transaction-content">
            <el-row :gutter="20">
              <el-col :span="14">
                <div class="form-section">
                  <div class="section-title">当前有效期</div>
                  <el-input v-model="endTime" :readonly="true" class="readonly-input"></el-input>
                  <div class="days-remaining">剩余天数：{{ daysRemaining || 0 }} 天</div>
                  <div class="section-title" style="margin-top: 20px;">新增数量</div>
                  <el-input-number
                    v-model="addStaffCount"
                    :min="1"
                    :max="100"
                    size="medium"
                    style="width: 200px;">
                  </el-input-number>
                  <div class="formula-hint">
                    计算公式：单价 ${{ extraUnitPrice }}/月 ÷ 30天 × 剩余 {{ daysRemaining || 0 }} 天
                  </div>
                </div>
              </el-col>
              <el-col :span="10">
                <div class="price-section" v-loading="addStaffCalculateLoading">
                  <div class="price-title">费用明细</div>
                  <div class="price-item" v-if="addStaffPriceData">
                    <span>新增人数：</span>
                    <span>{{ addStaffCount }} 人</span>
                  </div>
                  <div class="price-item" v-if="addStaffPriceData">
                    <span>单价：</span>
                    <span>${{ addStaffPriceData.unit_price || extraUnitPrice }}/月</span>
                  </div>
                  <div class="price-item" v-if="addStaffPriceData">
                    <span>剩余天数：</span>
                    <span>{{ addStaffPriceData.remaining_days || daysRemaining || 0 }} 天</span>
                  </div>
                  <div class="price-item" v-if="addStaffPriceData && addStaffPriceData.daily_price_per_staff">
                    <span>每人每天：</span>
                    <span>${{ parseFloat(addStaffPriceData.daily_price_per_staff).toFixed(4) }}</span>
                  </div>
                  <div class="price-item" v-if="!addStaffPriceData">
                    <span>新增人数：</span>
                    <span>{{ addStaffCount }} 人</span>
                  </div>
                  <div class="price-item" v-if="!addStaffPriceData">
                    <span>单价：</span>
                    <span>${{ extraUnitPrice }}/月</span>
                  </div>
                  <div class="price-item" v-if="!addStaffPriceData">
                    <span>剩余天数：</span>
                    <span>{{ daysRemaining || 0 }} 天</span>
                  </div>
                  <div class="price-total">
                    <span>合计应付：</span>
                    <span class="total-amount">${{ addStaffPrice }}</span>
                  </div>
                  <div class="price-hint" v-if="addStaffPriceData && addStaffPriceData.end_time">
                    有效期至：{{ addStaffPriceData.end_time }}
                  </div>
                  <el-button type="primary" size="medium" @click="handleAddStaff" :disabled="isExpired || loading || addStaffCalculateLoading || daysRemaining <= 0">立即支付</el-button>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 历史订单抽屉 -->
    <el-drawer
      title="历史订单"
      :visible.sync="orderDrawerVisible"
      direction="rtl"
      size="800px">
      <div class="order-drawer-content" v-loading="orderLoading">
        <el-table :data="orderList" style="width: 100%" size="small">
          <el-table-column prop="order_no" label="订单号" width="180"></el-table-column>
          <el-table-column prop="order_type" label="类型" width="100">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.order_type === 10 ? 'success' : 'warning'">
                {{ scope.row.order_type === 10 ? '购买套餐' : '加人' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="pay_price" label="支付金额" width="120">
            <template slot-scope="scope">
              ${{ scope.row.pay_price || '0.00' }}
            </template>
          </el-table-column>
          <el-table-column prop="pay_time" label="支付时间" width="150">
            <template slot-scope="scope">
              {{ formatTime(scope.row.pay_time) }}
            </template>
          </el-table-column>
          <el-table-column prop="pay_status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.pay_status === 20 ? 'success' : 'info'">
                {{ scope.row.pay_status === 20 ? '已支付' : '未支付' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="备注" show-overflow-tooltip></el-table-column>
        </el-table>
        <!-- 分页 -->
        <div class="pagination" style="margin-top: 20px;">
          <el-pagination
            @size-change="handleOrderSizeChange"
            @current-change="handleOrderCurrentChange"
            background
            :current-page="orderCurPage"
            :page-size="orderPageSize"
            layout="total, prev, pager, next, jumper"
            :total="orderTotal">
          </el-pagination>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import SubscriptionApi from '@/api/subscription.js';

export default {
  name: 'Subscription',
  data() {
    return {
      loading: true,
      orderLoading: false,
      /*Stripe 实例*/
      stripe: null,
      /*订阅信息*/
      subscription: {
        detail: null,
        active_technician_count: 0,
      },
      /*当前选中的Tab*/
      activeTab: 'renew',
      /*续费相关*/
      renewDuration: 1,
      renewCalculateLoading: false,
      renewPriceData: null, // 续费算价结果
      /*加人相关*/
      addStaffCount: 1,
      addStaffCalculateLoading: false,
      addStaffPriceData: null, // 购买名额算价结果
      /*订单抽屉*/
      orderDrawerVisible: false,
      orderList: [],
      orderCurPage: 1,
      orderPageSize: 15,
      orderTotal: 0,
    };
  },
  computed: {
    subscriptionDetail() {
      return this.subscription && this.subscription.detail ? this.subscription.detail : null;
    },
    planSnapshot() {
      if (!this.subscriptionDetail) return null;
      return this.subscriptionDetail.plan_snapshot || {};
    },
    basePrice() {
      const v = this.planSnapshot && this.planSnapshot.base_price;
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },
    extraUnitPrice() {
      const v = this.planSnapshot && this.planSnapshot.extra_staff_unit_price;
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },
    baseStaffCount() {
      const v = this.planSnapshot && this.planSnapshot.included_staff_count;
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },
    extraStaffCount() {
      const total = this.totalStaff;
      const base = this.baseStaffCount;
      return Math.max(0, total - base);
    },
    /*续费时按实际使用人数计算的额外名额数*/
    renewExtraStaffCount() {
      // 实际使用人数 - 基础名额 = 需要续费的额外名额数
      const used = this.usedStaff;
      const base = this.baseStaffCount;
      return Math.max(0, used - base);
    },
    /*续费后的总名额（按实际使用人数）*/
    renewAfterTotalStaff() {
      // 续费后总名额 = 基础名额 + 续费时的额外名额数
      return this.baseStaffCount + this.renewExtraStaffCount;
    },
    totalStaff() {
      const v = this.subscriptionDetail && this.subscriptionDetail.total_allowed_staff;
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },
    usedStaff() {
      const v = this.subscription && this.subscription.active_technician_count;
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },
    usagePercentage() {
      if (!this.totalStaff || this.totalStaff <= 0) return 0;
      const pct = Math.round((this.usedStaff / this.totalStaff) * 100);
      return Math.max(0, Math.min(100, pct));
    },
    usageStatus() {
      if (this.totalStaff > 0 && this.usedStaff >= this.totalStaff) return 'warning';
      return 'success';
    },
    endTime() {
      return this.subscriptionDetail && this.subscriptionDetail.end_time ? this.subscriptionDetail.end_time : '';
    },
    status() {
      return this.subscriptionDetail && typeof this.subscriptionDetail.status !== 'undefined'
        ? Number(this.subscriptionDetail.status)
        : null;
    },
    daysRemaining() {
      const end = this.parseDateTime(this.endTime);
      if (!end) return 0;
      const diffMs = end.getTime() - Date.now();
      return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
    },
    isExpiringSoon() {
      return this.status === 10
        && typeof this.daysRemaining === 'number'
        && this.daysRemaining >= 0
        && this.daysRemaining <= 7;
    },
    isExpired() {
      return this.status === 30;
    },
    subscriptionTagText() {
      if (this.isExpired) return '已到期';
      if (this.isExpiringSoon) return '快到期';
      if (this.status === 10) return '生效中';
      return '未知';
    },
    subscriptionTagType() {
      if (this.isExpired) return 'danger';
      if (this.isExpiringSoon) return 'warning';
      return 'success';
    },
    /*续费总价（使用后端算价结果）*/
    renewTotalPrice() {
      if (this.renewPriceData && this.renewPriceData.total_price) {
        return parseFloat(this.renewPriceData.total_price).toFixed(2);
      }
      // 兜底：前端计算（仅预览）
      const base = this.basePrice * this.renewDuration;
      const extra = this.renewExtraStaffCount * this.extraUnitPrice * this.renewDuration;
      return (base + extra).toFixed(2);
    },
    basePriceDisplay() {
      if (this.renewPriceData && this.renewPriceData.base_price) {
        return parseFloat(this.renewPriceData.base_price).toFixed(2);
      }
      return (this.basePrice * this.renewDuration).toFixed(2);
    },
    extraStaffPrice() {
      if (this.renewPriceData && this.renewPriceData.extra_price) {
        return parseFloat(this.renewPriceData.extra_price).toFixed(2);
      }
      return (this.renewExtraStaffCount * this.extraUnitPrice * this.renewDuration).toFixed(2);
    },
    /*加人总价（使用后端算价结果）*/
    addStaffPrice() {
      if (this.addStaffPriceData && this.addStaffPriceData.total_price) {
        return parseFloat(this.addStaffPriceData.total_price).toFixed(2);
      }
      // 兜底：前端计算（仅预览）
      if (this.daysRemaining <= 0) return '0.00';
      const dailyPrice = this.extraUnitPrice / 30;
      const total = this.addStaffCount * dailyPrice * this.daysRemaining;
      return total.toFixed(2);
    },
  },
  watch: {
    /*监听续费时长变化，实时算价*/
    renewDuration() {
      if (this.renewDuration > 0) {
        this.calculateRenewPrice();
      }
    },
    /*监听购买名额数量变化，实时算价*/
    addStaffCount() {
      if (this.addStaffCount > 0 && this.daysRemaining > 0) {
        this.calculateAddStaffPrice();
      }
    },
    /*监听Tab切换，切换时重新算价*/
    activeTab(newVal) {
      if (newVal === 'renew' && this.renewDuration > 0) {
        this.calculateRenewPrice();
      } else if (newVal === 'addStaff' && this.addStaffCount > 0 && this.daysRemaining > 0) {
        this.calculateAddStaffPrice();
      }
    },
  },
  created() {
    console.log("getSubscriptionPageUrl()::", this.getSubscriptionPageUrl())
    // 检查路由参数，自动切换到指定Tab
    if (this.$route.query.tab) {
      const tab = this.$route.query.tab;
      if (tab === 'addStaff' || tab === 'renew') {
        this.activeTab = tab;
      }
    }
    // 初始化 Stripe
    if (typeof Stripe === 'function') {
      this.stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
    }
    // 支付成功/取消后的提示与数据刷新
    if (this.$route.query.pay_success === '1') {
      this.$message({
        message: '支付成功，权益已更新（如有延迟请稍后刷新）',
        type: 'success'
      });
    } else if (this.$route.query.pay_cancel === '1') {
      this.$message({
        message: '支付未完成',
        type: 'warning'
      });
    }
    // 清理 URL 中的支付回跳参数，避免刷新后重复提示
    if (this.$route.query.pay_success || this.$route.query.pay_cancel) {
      const { pay_success, pay_cancel, ...restQuery } = this.$route.query;
      this.$router.replace({ path: this.$route.path, query: restQuery });
    }
    this.getData();
  },
  methods: {
    /*获取数据*/
    getData() {
      let self = this;
      self.loading = true;
      SubscriptionApi.getSubscription({}, true)
        .then(data => {
          self.loading = false;
          if (data.code === 1 && data.data && data.data.subscription) {
            self.subscription = {
              detail: data.data.subscription,
              active_technician_count: data.data.active_technician_count || 0,
            };
            // 数据加载完成后，根据当前Tab自动算价
            self.$nextTick(() => {
              if (self.activeTab === 'renew' && self.renewDuration > 0) {
                self.calculateRenewPrice();
              } else if (self.activeTab === 'addStaff' && self.addStaffCount > 0 && self.daysRemaining > 0) {
                self.calculateAddStaffPrice();
              }
            });
          }
        })
        .catch(error => {
          self.loading = false;
        });
    },
    /*解析日期时间*/
    parseDateTime(str) {
      if (!str) return null;
      const normalized = String(str).trim().replace(' ', 'T');
      const d = new Date(normalized);
      if (!isNaN(d.getTime())) return d;
      const fallback = new Date(String(str).trim().replace(/-/g, '/'));
      return isNaN(fallback.getTime()) ? null : fallback;
    },
    /*Tab切换*/
    handleTabClick(tab) {
      // Tab切换时，可以在这里做一些逻辑处理
    },
    /*续费算价*/
    calculateRenewPrice() {
      if (this.renewDuration <= 0) {
        this.renewPriceData = null;
        return;
      }
      let self = this;
      self.renewCalculateLoading = true;
      SubscriptionApi.calculateRenew({
        order_type: 10,
        month: self.renewDuration
      }, true)
        .then(data => {
          self.renewCalculateLoading = false;
          if (data.code === 1 && data.data) {
            self.renewPriceData = data.data;
          } else {
            self.renewPriceData = null;
            self.$message && self.$message({
              message: data.msg || '算价失败，请重试',
              type: 'warning'
            });
          }
        })
        .catch(error => {
          self.renewCalculateLoading = false;
          self.renewPriceData = null;
        });
    },
    /*购买名额算价*/
    calculateAddStaffPrice() {
      if (this.addStaffCount <= 0 || this.daysRemaining <= 0) {
        this.addStaffPriceData = null;
        return;
      }
      let self = this;
      self.addStaffCalculateLoading = true;
      SubscriptionApi.calculateAddStaff({
        order_type: 20,
        add_staff_count: self.addStaffCount
      }, true)
        .then(data => {
          self.addStaffCalculateLoading = false;
          if (data.code === 1 && data.data) {
            self.addStaffPriceData = data.data;
          } else {
            self.addStaffPriceData = null;
            self.$message && self.$message({
              message: data.msg || '算价失败，请重试',
              type: 'warning'
            });
          }
        })
        .catch(error => {
          self.addStaffCalculateLoading = false;
          self.addStaffPriceData = null;
        });
    },
    /*构造权益页完整 URL（用于 Stripe 回跳）*/
    getSubscriptionPageUrl() {
      // 直接使用当前 URL 的基础部分（去掉 hash 和 query），然后加上目标路由的 hash
      // 这样可以确保包含完整的路径（如 /supplier）
      const currentUrl = window.location.href;
      const baseUrl = currentUrl.split('#')[0].split('?')[0]; // 去掉 hash 和 query 参数
      // 获取路由的 hash 路径（在 hash 模式下，resolve 返回的 href 包含 #）
      const resolved = this.$router.resolve({ path: '/setting/subscription/index' });
      const hashPath = resolved && resolved.href ? resolved.href : '#/setting/subscription/index';
      // 拼接完整 URL
      return baseUrl + hashPath;
    },
    /*通用：创建 Stripe Session 并跳转*/
    async createStripeAndRedirect(payload) {
      if (!this.stripe) {
        this.$message({
          message: '支付组件未初始化，请稍后重试或联系管理员',
          type: 'error'
        });
        return;
      }
      try {
        this.loading = true;
        const baseUrl = this.getSubscriptionPageUrl();
        const success_url = baseUrl + (baseUrl.indexOf('?') > -1 ? '&' : '?') + 'pay_success=1';
        const cancel_url = baseUrl + (baseUrl.indexOf('?') > -1 ? '&' : '?') + 'pay_cancel=1';
        const resp = await SubscriptionApi.createStripeSession({
          ...payload,
          success_url,
          cancel_url,
        }, true);
        this.loading = false;
        if (!resp || resp.code !== 1 || !resp.data || !resp.data.session_id) {
          this.$message({
            message: (resp && resp.msg) || '创建支付会话失败，请重试',
            type: 'error'
          });
          return;
        }
        const sessionId = resp.data.session_id;
        const result = await this.stripe.redirectToCheckout({ sessionId });
        if (result && result.error) {
          this.$message({
            message: '跳转支付失败：' + result.error.message,
            type: 'error'
          });
        }
      } catch (e) {
        this.loading = false;
        this.$message({
          message: '创建支付会话失败，请重试',
          type: 'error'
        });
      }
    },
    /*续费支付*/
    async handleRenew() {
      if (!this.renewPriceData || !this.renewPriceData.total_price) {
        this.$message({
          message: '请先等待价格计算完成',
          type: 'warning'
        });
        return;
      }
      await this.createStripeAndRedirect({
        order_type: 10,
        month: this.renewDuration,
      });
    },
    /*购买名额支付*/
    async handleAddStaff() {
      if (!this.addStaffPriceData || !this.addStaffPriceData.total_price) {
        this.$message({
          message: '请先等待价格计算完成',
          type: 'warning'
        });
        return;
      }
      if (this.daysRemaining <= 0) {
        this.$message({
          message: '当前套餐已过期，无法单独购买名额，请先续费',
          type: 'warning'
        });
        return;
      }
      await this.createStripeAndRedirect({
        order_type: 20,
        add_staff_count: this.addStaffCount,
      });
    },
    /*打开订单抽屉*/
    openOrderDrawer() {
      this.orderDrawerVisible = true;
      this.getOrderList();
    },
    /*获取订单列表*/
    getOrderList() {
      let self = this;
      self.orderLoading = true;
      let params = {
        page: self.orderCurPage,
        list_rows: self.orderPageSize,
      };
      SubscriptionApi.getOrderList(params, true)
        .then(data => {
          self.orderLoading = false;
          if (data.code === 1 && data.data && data.data.list) {
            self.orderList = data.data.list.data || [];
            self.orderTotal = data.data.list.total || 0;
          }
        })
        .catch(error => {
          self.orderLoading = false;
        });
    },
    /*订单分页 - 每页多少条*/
    handleOrderSizeChange(val) {
      this.orderCurPage = 1;
      this.orderPageSize = val;
      this.getOrderList();
    },
    /*订单分页 - 翻页*/
    handleOrderCurrentChange(val) {
      this.orderCurPage = val;
      this.getOrderList();
    },
    /*格式化时间戳*/
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
  }
};
</script>

<style lang="scss" scoped>
.subscription-page {
  padding: 20px;
}

.overview-card {
  margin-bottom: 20px;

  .overview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .overview-title {
      font-size: 16px;
      font-weight: bold;
    }
  }

  .overview-left {
    .plan-name {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 10px;
    }

    .plan-status {
      margin-bottom: 15px;
    }

    .plan-expiry {
      color: #666;
      font-size: 14px;

      &.expiring-soon {
        color: #f56c6c;
        font-weight: bold;
      }
    }
  }

  .overview-right {
    .staff-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;

      .staff-label {
        font-size: 14px;
        color: #666;
      }

      .staff-count {
        font-size: 18px;
        font-weight: bold;
        color: #333;
      }
    }

    .staff-detail {
      margin-top: 10px;
      font-size: 12px;
      color: #999;
    }
  }
}

.transaction-card {
  .transaction-content {
    padding: 20px 0;
  }

  .form-section {
    .section-title {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 15px;
    }

    .current-config {
      margin-top: 20px;
      padding: 15px;
      background: #f5f7fa;
      border-radius: 4px;
      font-size: 14px;
      color: #666;

      p {
        margin: 5px 0;
        line-height: 1.6;
      }

      .renew-hint {
        color: #409eff;
        font-weight: bold;
        margin-top: 10px;
      }

      .renew-warning {
        margin-top: 10px;
        padding: 8px;
        background: #fff3cd;
        border: 1px solid #ffc107;
        border-radius: 4px;
        color: #856404;
        font-size: 13px;

        i {
          margin-right: 5px;
        }
      }
    }

    .readonly-input {
      margin-bottom: 10px;
    }

    .days-remaining {
      margin-top: 10px;
      font-size: 14px;
      color: #666;
    }

    .formula-hint {
      margin-top: 15px;
      font-size: 12px;
      color: #999;
    }
  }

  .price-section {
    padding: 20px;
    background: #f5f7fa;
    border-radius: 4px;

    .price-title {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 15px;
    }

    .price-item {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      font-size: 14px;
      color: #666;
      flex-wrap: wrap;

      .price-detail {
        width: 100%;
        font-size: 12px;
        color: #999;
        margin-top: 5px;
      }
    }

    .price-total {
      display: flex;
      justify-content: space-between;
      margin-top: 20px;
      padding-top: 15px;
      border-top: 1px solid #ddd;
      font-size: 16px;
      font-weight: bold;

      .total-amount {
        color: #f56c6c;
        font-size: 20px;
      }
    }

    .price-hint {
      margin-top: 10px;
      font-size: 12px;
      color: #999;
      text-align: center;
    }

    .el-button {
      width: 100%;
      margin-top: 20px;
    }
  }
}

.order-drawer-content {
  padding: 10px;
}
</style>



// WEBPACK FOOTER //
// src/views/setting/subscription/index.vue